var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { format, isPast } from "date-fns";
import { IdentifiedPatientState } from "healthex-common/consent.dtos";
import { SHLLinkStatus } from "healthex-common/shl.dtos";
import { slugifyOptional, unslugifyOptional } from "healthex-common/utils";
import { action, computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SHLUIManager } from "../Logic/SHLUIManager";
import { NamedRoutes } from "./NamedRoutes";
import { getObservableT, i18nGetDateFnsLocale } from "../Infra/i18next";
import { getTranslatedStudyText } from "./TranslationService";
import { getPatientStatusChip } from "../patient.utils";
const t = getObservableT("patients");
export var SharingSection;
(function (SharingSection) {
    SharingSection["CONNECTIONS"] = "CONNECTIONS";
    SharingSection["SHARED_LINKS"] = "SHARED_LINKS";
})(SharingSection || (SharingSection = {}));
export function parseSharingSection(slug) {
    const section = unslugifyOptional(slug);
    if (section && Object.values(SharingSection).includes(section))
        return section;
    return SharingSection.CONNECTIONS;
}
export var ConnectionSection;
(function (ConnectionSection) {
    ConnectionSection["ACTION_NEEDED"] = "ACTION_NEEDED";
    ConnectionSection["ACTIVE"] = "ACTIVE";
    ConnectionSection["INACTIVE"] = "INACTIVE";
})(ConnectionSection || (ConnectionSection = {}));
export class PatientStudiesListViewModel {
    get currentSection() {
        var _a;
        return parseSharingSection((_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.section);
    }
    setSection(section) {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SHARING, {
            section: slugifyOptional(section),
        });
    }
    constructor(container) {
        this.inactiveLinksExpanded = false;
        this.detailsDialogOpen = false;
        this.detailsDialogStudyId = null;
        this.patientResearchDashboardUIManager = container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.browserLanguageUIManager = container.browserLanguageUIManager;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.shlUIManager = container.shlUIManager;
        this.bannerUIManager = container.bannerUIManager;
    }
    run() { }
    stop() {
        this.closeDetailsDialog();
    }
    get patientSHLs() {
        return this.shlUIManager.patientSHLsForSearch.value;
    }
    get activeSHLs() {
        if (this.patientSHLs === NOT_LOADED || !Array.isArray(this.patientSHLs))
            return [];
        return this.patientSHLs
            .filter((shl) => shl.status === SHLLinkStatus.ACTIVE && !isPast(new Date(shl.expiresAt)))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    get inactiveSHLs() {
        if (this.patientSHLs === NOT_LOADED || !Array.isArray(this.patientSHLs))
            return [];
        return this.patientSHLs
            .filter((shl) => shl.status !== SHLLinkStatus.ACTIVE || isPast(new Date(shl.expiresAt)))
            .sort((a, b) => {
            const getRelevantDate = (shl) => {
                if (shl.revokedAt)
                    return new Date(shl.revokedAt);
                if (shl.usedAt)
                    return new Date(shl.usedAt);
                return new Date(shl.expiresAt);
            };
            const dateA = getRelevantDate(a);
            const dateB = getRelevantDate(b);
            return dateB.getTime() - dateA.getTime();
        });
    }
    get hasActiveOrInactiveSHLs() {
        return this.activeSHLs.length > 0 || this.inactiveSHLs.length > 0;
    }
    setInactiveLinksExpanded(expanded) {
        this.inactiveLinksExpanded = expanded;
    }
    getSHLListItemInfo(shl, isActive) {
        var _a;
        const formatMMDDYYYY = (date) => format(new Date(date), "MM/dd/yyyy", { locale: i18nGetDateFnsLocale() });
        const title = ((_a = shl.label) === null || _a === void 0 ? void 0 : _a.trim()) ? shl.label : t("Created on {{date}}", { date: formatMMDDYYYY(shl.createdAt) });
        const scopes = SHLUIManager.removeImpliedScopes(shl.consentDataResourceScopes);
        const description = shl.fullRecordWithoutRestrictedData
            ? t("All health records included")
            : t("{{count}} data types included", {
                count: scopes.length,
            });
        let statusLine;
        if (isActive) {
            statusLine = shl.isOneTimeUse
                ? t("Active · One-time use")
                : t("Active · Expires {{date}}", { date: formatMMDDYYYY(shl.expiresAt) });
        }
        else {
            if (shl.status === SHLLinkStatus.USED) {
                statusLine = !shl.usedAt ? t("Used on unknown date") : t("Used on {{date}}", { date: formatMMDDYYYY(shl.usedAt) });
            }
            else if (shl.status === SHLLinkStatus.REVOKED) {
                statusLine = t("Deactivated by you");
            }
            else {
                statusLine = t("Expired on {{date}}", { date: formatMMDDYYYY(shl.expiresAt) });
            }
        }
        return { title, description, statusLine };
    }
    goToSHLLink(shlId) {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SMART_LINK_DETAILS, { shlId });
    }
    goToCreateShareLink() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_CREATE_SMART_LINK);
    }
    get isOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false;
    }
    get noConsentsAvailable() {
        return this.patientResearchDashboardUIManager.noConsentsAvailable;
    }
    get zeroState() {
        if (this.isOptedOut === NOT_LOADED)
            return null;
        if (this.isOptedOut) {
            return {
                title: t("You're not receiving requests right now"),
                text: t("Your health records can help {{ organizationName }} deliver data-driven, proactive care that supports your health journey\n\nYou’ve opted out of HealthEx, but you can update this anytime in your settings.", { organizationName: this.authManager.orgName }),
                buttonText: t("Join HealthEx again"),
                buttonOnClick: () => this.optInAgain(),
            };
        }
        return null;
    }
    get studies() {
        return this.patientResearchDashboardUIManager.sortedStudies;
    }
    get connectionsBySection() {
        var _a, _b;
        if (this.studies === NOT_LOADED || !Array.isArray(this.studies))
            return [];
        const actionNeeded = [];
        const active = [];
        const inactive = [];
        const actionNeededStudyIds = new Set();
        for (const entry of this.studies) {
            const study = entry.study;
            const consent = entry.consents[0];
            if (consent === undefined)
                continue;
            const title = this.getStudyPatientFriendlyTitle(study);
            const orgLogo = (_a = study.organizationIconUrl) !== null && _a !== void 0 ? _a : null;
            const orgLogoRectangular = (_b = study.organizationRectangularIconUrl) !== null && _b !== void 0 ? _b : null;
            const openDetails = () => this.openPatientStudyDetailsPage(study.id);
            const cardId = consent.pending ? `${study.id}-pending` : study.id;
            if (!!consent.pending ||
                consent.state === IdentifiedPatientState.ENROLLING ||
                consent.state === IdentifiedPatientState.ENROLLING_INTERESTED ||
                consent.state === IdentifiedPatientState.ENROLLING_CONTACTED ||
                (consent.state === IdentifiedPatientState.ENROLLED && consent.updateRecordsNeeded)) {
                actionNeededStudyIds.add(study.id);
                actionNeeded.push({
                    id: cardId,
                    title,
                    orgLogo,
                    orgLogoRectangular,
                    chip: getPatientStatusChip(consent),
                    subtitle: this.getStudyReasonForRequest(study),
                    subtitleIsError: true,
                    cta: openDetails,
                });
            }
            else if (consent.state === IdentifiedPatientState.ENROLLED) {
                const dateLabel = format(new Date(consent.timestamp), "MMM d", { locale: i18nGetDateFnsLocale() });
                active.push({
                    id: cardId,
                    title,
                    orgLogo,
                    orgLogoRectangular,
                    chip: getPatientStatusChip(consent),
                    subtitle: `${t("Connected")} ${dateLabel}`,
                    subtitleIsError: false,
                    cta: openDetails,
                });
            }
            else if (consent.state === IdentifiedPatientState.DECLINED_ENROLLMENT ||
                consent.state === IdentifiedPatientState.REMOVED ||
                consent.state === IdentifiedPatientState.REVOKED_ENROLLMENT) {
                const dateLabel = format(new Date(consent.timestamp), "MMM d", { locale: i18nGetDateFnsLocale() });
                inactive.push({
                    id: cardId,
                    title,
                    orgLogo,
                    orgLogoRectangular,
                    chip: getPatientStatusChip(consent),
                    subtitle: this.getInactiveSubtitle(consent.state, dateLabel),
                    subtitleIsError: false,
                    cta: openDetails,
                });
            }
        }
        const activeDeduped = active.filter((item) => !actionNeededStudyIds.has(item.id));
        const inactiveDeduped = inactive.filter((item) => !actionNeededStudyIds.has(item.id));
        const sections = [];
        if (actionNeeded.length > 0)
            sections.push({ section: ConnectionSection.ACTION_NEEDED, label: t("Action Needed"), items: actionNeeded });
        if (activeDeduped.length > 0)
            sections.push({ section: ConnectionSection.ACTIVE, label: t("Active"), items: activeDeduped });
        if (inactiveDeduped.length > 0)
            sections.push({ section: ConnectionSection.INACTIVE, label: t("Inactive"), items: inactiveDeduped });
        return sections;
    }
    get availableAIPartners() {
        return this.patientResearchDashboardUIManager.filteredAvailablePartners;
    }
    async optInAgain() {
        await this.patientPreferencesManager.updatePatientPreferences({ optedOut: false });
    }
    getInactiveSubtitle(state, dateLabel) {
        switch (state) {
            case IdentifiedPatientState.DECLINED_ENROLLMENT:
                return `${t("Declined")} ${dateLabel}`;
            case IdentifiedPatientState.REMOVED:
                return `${t("Removed")} ${dateLabel}`;
            case IdentifiedPatientState.REVOKED_ENROLLMENT:
                return `${t("Ended")} ${dateLabel}`;
            default:
                return dateLabel;
        }
    }
    getStudyPatientFriendlyTitle(study) {
        const translatedTitle = getTranslatedStudyText(study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage);
        return translatedTitle || study.patientFriendlyTitle || study.title || "";
    }
    getStudyReasonForRequest(study) {
        const translatedRequestReason = getTranslatedStudyText(study, "purposeOfDataBriefEducation", this.browserLanguageUIManager.currentLanguage);
        return translatedRequestReason || "";
    }
    startConsentFlow(studyId) {
        this.patientConsentUIManager.startConsentFlow(studyId, null, null);
    }
    openPatientStudyDetailsPage(studyId) {
        this.detailsDialogStudyId = studyId;
        this.detailsDialogOpen = true;
    }
    closeDetailsDialog() {
        this.detailsDialogOpen = false;
        this.detailsDialogStudyId = null;
    }
    connectToPartner(partner) {
        if (partner.aiChatbotUrl) {
            window.open(partner.aiChatbotUrl, "_blank");
        }
        else {
            this.bannerUIManager.displayBanner({
                type: "ERROR",
                text: t("Unable to connect. Please try again later."),
            });
        }
    }
}
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "inactiveLinksExpanded", void 0);
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "detailsDialogOpen", void 0);
__decorate([
    observable
], PatientStudiesListViewModel.prototype, "detailsDialogStudyId", void 0);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "currentSection", null);
__decorate([
    action
], PatientStudiesListViewModel.prototype, "setSection", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "patientSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "activeSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "inactiveSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "hasActiveOrInactiveSHLs", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "isOptedOut", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "noConsentsAvailable", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "zeroState", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "studies", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "connectionsBySection", null);
__decorate([
    computed
], PatientStudiesListViewModel.prototype, "availableAIPartners", null);
__decorate([
    action
], PatientStudiesListViewModel.prototype, "openPatientStudyDetailsPage", null);
__decorate([
    action
], PatientStudiesListViewModel.prototype, "closeDetailsDialog", null);
__decorate([
    action
], PatientStudiesListViewModel.prototype, "connectToPartner", null);
